/**
 * useCreateReservation — TanStack mutation hook over
 * `POST /organizations/{slug}/reservations` (SCRUM-337).
 *
 * The create-reservation form drives this with two submit buttons:
 *   - "Create quote"    → `instantBook: false` → a QUO (quote) reservation.
 *   - "Confirm booking" → `instantBook: true`  → a BKD (booked) reservation
 *                          (atomic reserve; a calendar-lock race → 409).
 *
 * CRITICAL — booking OCC surfaces as a server-side 409, never a 412
 * (`domain/reservation/errors.ts`, SCRUM-277): the expected version is
 * captured INSIDE the tx, so the client sends NO `If-Match` header and NO
 * version var. A concurrent-write / dates-unavailable race is a plain 409
 * with a stable body. The hook therefore branches on 409 (`conflict`), NOT
 * on 412 — mirroring `useReservationLifecycle`'s `kindFor`.
 *
 * The form works in a two-axis occupancy model (adults + children); the
 * wire works in a `guestCount` (total, drives fees) + `childCount` (frozen
 * §2.12 StayContext subset) model. The hook maps at the boundary:
 *   - `guestCount = adults + children`  (total party size)
 *   - `childCount = children`
 * There is NEVER a `children` key on the wire body — only `guestCount` +
 * `childCount`.
 *
 * The guest is always inline-created via the new-guest binding arm
 * (`guest: { displayName, email, phone }`). `heardAbout` is sent only when
 * present (omitted when the reservation-type does not require it).
 *
 * mutationFn:
 *   - POSTs `/organizations/{slug}/reservations` with `params.path` only
 *     (NO header params → NO `If-Match`) and the mapped body,
 *   - unwraps the double envelope on success: the openapi-fetch
 *     `{ data, response, error }` union's `data` is the API envelope
 *     `{ data: <result> }` → resolves `result.data.data`,
 *   - maps errors to a tagged union `{ kind }`: 403 → `forbidden`,
 *     409 → `conflict`, 422 → `illegal`, 404 → `notFound`, any other
 *     non-ok (incl. an unexpected 400) → `error`. There is NO `invalidStay`
 *     kind — that is `usePreview` only.
 *   - on success `await`s `invalidateQueries` for `bookingQueryKeys.lists()`
 *     so the grid picks up the new row.
 *
 * NEVER fakes success; NEVER optimistically inserts a row.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { bookingQueryKeys } from './queryKeys.js';

/** Tagged error kinds the form branches on. NO `invalidStay` (preview only). */
export type CreateErrorKind =
  | 'forbidden' // 403 — lacks the create permission slug
  | 'conflict' // 409 — dates unavailable / calendar-lock / OCC race
  | 'illegal' // 422 — validation (heard_about_required / ChannelCollect-only)
  | 'notFound' // 404 — absent property / cross-org (anti-enum)
  | 'error'; // any other non-ok (incl. an unexpected 400)

/** A create failure carrying the mapped `kind` for the UI. */
export interface CreateError extends Error {
  readonly kind: CreateErrorKind;
  /**
   * The machine `error` code from the response body (booking's error filter
   * emits `{ error: '<code>' }`), e.g. `heard_about_required`,
   * `channel_collect_not_locally_creatable`, `unknown_reservation_type`. Lets the
   * form show a SPECIFIC message per 422 cause instead of one generic line;
   * `undefined` when the body carries no code.
   */
  readonly code?: string;
}

/**
 * The form-facing create input. `adults` + `children` are the two-axis
 * occupancy model the form tracks; the hook maps them to the wire
 * `guestCount` (= adults + children) + `childCount` (= children).
 */
export interface CreateInput {
  readonly propertyId: string;
  readonly checkInDate: string;
  readonly checkOutDate: string;
  /** Adults (>= 1). Total `guestCount` = adults + children. */
  readonly adults: number;
  /** Children — form-internal; maps to `childCount` on the wire. */
  readonly children: number;
  /** Pets (>= 0). */
  readonly petCount: number;
  /** Vehicles (>= 0). */
  readonly vehicleCount: number;
  /** Opaque reservation-type discriminator, or null. */
  readonly reservationType: string | null;
  /** Opaque booking-channel discriminator, or null. */
  readonly bookingChannel: string | null;
  /** false → "Create quote" (QUO); true → "Confirm booking" (BKD). */
  readonly instantBook: boolean;
  /** New-guest binding arm — inline-created guest. */
  readonly guest: {
    /** `"{firstName} {lastName}"` composed by the form. */
    readonly displayName: string;
    readonly email: string | null;
    readonly phone: string | null;
  };
  /** Provenance axis (§2.9). Omitted from the wire when undefined. */
  readonly heardAbout?: string;
}

/** The double-unwrapped create result. */
export interface CreateResult {
  readonly reservationId: string;
  readonly version: number;
  readonly status?: string;
  readonly grandTotalCents?: number;
}

/** Maps an HTTP status to the tagged error kind (booking OCC = 409). */
function kindFor(status: number): CreateErrorKind {
  if (status === 409) return 'conflict';
  if (status === 422) return 'illegal';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  return 'error';
}

/** Read the machine `error` code from a booking error body (`{ error: '<code>' }`). */
function extractErrorCode(body: unknown): string | undefined {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    const code = (body as { error?: unknown }).error;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Builds a `CreateError` for a mapped HTTP failure, carrying the body's code. */
function createError(status: number, code?: string): CreateError {
  const err = new Error(`create reservation failed (${status})`) as Error & {
    kind: CreateErrorKind;
    code?: string;
  };
  err.kind = kindFor(status);
  if (code !== undefined) err.code = code;
  return err;
}

/**
 * The narrow structural shape of the openapi-fetch create POST result that
 * the unwrap reads. The endpoint returns the `{ data: <result> }` API
 * envelope in the openapi-fetch `{ data, response, error }` union; this is
 * the minimal surface the unwrap touches (typing against `typeof
 * apiClient.POST<…>` is not possible — the generic `ClientMethod` overload
 * set has no `typeof`-applicable signature).
 */
interface CreatePostResult {
  readonly response: Response;
  readonly data?: { readonly data: CreateResult } | undefined;
  /** openapi-fetch parks the parsed error body here on a non-2xx response. */
  readonly error?: unknown;
}

export function useCreateReservation(): UseMutationResult<CreateResult, CreateError, CreateInput> {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  return useMutation<CreateResult, CreateError, CreateInput>({
    mutationFn: async (input) => {
      if (orgSlug === null) throw createError(0);

      const body: Record<string, unknown> = {
        propertyId: input.propertyId,
        checkInDate: input.checkInDate,
        checkOutDate: input.checkOutDate,
        // Total party size drives fees; childCount is the frozen subset.
        // NO `children` key on the wire — only guestCount + childCount.
        guestCount: input.adults + input.children,
        childCount: input.children,
        petCount: input.petCount,
        vehicleCount: input.vehicleCount,
        reservationType: input.reservationType,
        bookingChannel: input.bookingChannel,
        instantBook: input.instantBook,
        guest: {
          displayName: input.guest.displayName,
          email: input.guest.email,
          phone: input.guest.phone,
        },
      };
      // heardAbout is sent only when present (omitted, not empty-string,
      // when the reservation-type does not require it).
      if (input.heardAbout !== undefined) {
        body.heardAbout = input.heardAbout;
      }

      const result = (await apiClient.POST('/organizations/{slug}/reservations', {
        // params.path ONLY — no header params → NO If-Match (server-side 409).
        params: { path: { slug: orgSlug } },
        body: body as never,
      })) as CreatePostResult;

      if (!result.response.ok || result.data === undefined) {
        throw createError(result.response.status, extractErrorCode(result.error));
      }

      await queryClient.invalidateQueries({
        queryKey: bookingQueryKeys.lists(),
      });

      return result.data.data;
    },
  });
}
